import type { GatewaySettlementAccount } from '@/types/composition-functions';
import { firstValidationMessage, toUserMessage } from '@/utils/error';
import { normalizeCode } from '@/utils/billingLabels';

/**
 * Logic behind the "PAY Items" panel under a payment provider in
 * Settings → Payment (GatewaySettlementAccountsPanel.vue): the fields each
 * provider needs, which are required, the payload sent to the server, and the
 * plain-English copy for toasts and confirmations.
 */

// @allow-handwritten UI-only field descriptor for the dynamic provider config form; never crosses the wire
export interface SettlementConfigField {
  key: string;
  label: string;
  help: string;
  required: boolean;
  secret: boolean;
}

const KEPT_PRIVATE = 'Kept private — never shown again after you save.';

/*
 * Interswitch has no "Environment" field: whether payments are test or live is
 * decided by the server the school runs on, not by a setting here (the
 * backend never reads a per-account environment).
 */
const PROVIDER_CONFIGS: Record<string, SettlementConfigField[]> = {
  interswitch: [
    {
      key: 'merchantCode',
      label: 'Merchant Code',
      help: 'Your Interswitch merchant code, e.g. MX12345. Interswitch sends it when your merchant account is approved.',
      required: true,
      secret: false,
    },
    { key: 'payItemId', label: 'PAY Item ID', help: 'The ID of the PAY Item set up for this bank account on your Interswitch merchant dashboard.', required: true, secret: false },
    {
      key: 'macKey',
      label: 'MAC Key',
      help: `The secret key Interswitch gives with the PAY Item. It proves payments really came from you. ${KEPT_PRIVATE}`,
      required: true,
      secret: true,
    },
    { key: 'apiKey', label: 'API Key', help: `Only needed if Interswitch gave you one for this PAY Item. ${KEPT_PRIVATE}`, required: false, secret: true },
  ],
  paystack: [
    { key: 'publicKey', label: 'Public Key', help: 'From Paystack: Settings → API Keys & Webhooks.', required: true, secret: false },
    { key: 'secretKey', label: 'Secret Key', help: `From the same Paystack page. ${KEPT_PRIVATE}`, required: true, secret: true },
  ],
  flutterwave: [
    { key: 'publicKey', label: 'Public Key', help: 'From Flutterwave: Settings → API Keys.', required: true, secret: false },
    { key: 'secretKey', label: 'Secret Key', help: `From the same Flutterwave page. ${KEPT_PRIVATE}`, required: true, secret: true },
    { key: 'encryptionKey', label: 'Encryption Key', help: `From the same Flutterwave page. ${KEPT_PRIVATE}`, required: false, secret: true },
  ],
  monnify: [
    { key: 'apiKey', label: 'API Key', help: 'From Monnify: Settings → API Keys & Webhooks.', required: true, secret: false },
    { key: 'secretKey', label: 'Secret Key', help: `From the same Monnify page. ${KEPT_PRIVATE}`, required: true, secret: true },
    { key: 'contractCode', label: 'Contract Code', help: 'From the same Monnify page.', required: true, secret: false },
  ],
  remita: [
    { key: 'serviceTypeId', label: 'Service Type ID', help: 'The ID of the service you created on Remita.', required: true, secret: false },
    { key: 'merchantId', label: 'Merchant ID', help: 'Your Remita merchant number.', required: true, secret: false },
    { key: 'apiKey', label: 'API Key', help: `From your Remita integration settings. ${KEPT_PRIVATE}`, required: true, secret: true },
  ],
};

/** The config key whose value identifies the account at the gateway. */
const IDENTIFIER_KEY: Record<string, string> = {
  interswitch: 'payItemId',
  paystack: 'publicKey',
  flutterwave: 'publicKey',
  monnify: 'contractCode',
  remita: 'serviceTypeId',
};

export function configFieldsFor(providerName: string | null | undefined): SettlementConfigField[] {
  return PROVIDER_CONFIGS[normalizeCode(providerName)] ?? [];
}

export function identifierKeyFor(providerName: string | null | undefined): string {
  return IDENTIFIER_KEY[normalizeCode(providerName)] ?? configFieldsFor(providerName)[0]?.key ?? '';
}

// @allow-handwritten UI-only section copy; never crosses the wire
export interface SettlementSectionCopy {
  title: string;
  description: string;
  /** What one row is called in buttons and toasts, e.g. "PAY Item". */
  noun: string;
}

export function settlementSectionCopy(providerName: string | null | undefined): SettlementSectionCopy {
  if (normalizeCode(providerName) === 'interswitch') {
    return {
      title: 'PAY Items',
      description: 'Each PAY Item sends payments into a different bank account. A fee uses the default PAY Item unless a different one is chosen for it.',
      noun: 'PAY Item',
    };
  }
  return {
    title: 'Pays-into accounts',
    description: 'Each one sends payments into a different bank account.',
    noun: 'Pays-into account',
  };
}

/*
 * Secrets come back masked to '' whether or not one was ever saved (the
 * backend returns every config key), so the screen can't tell a saved secret
 * from a missing one. On edit a blank secret keeps whatever is stored, so it
 * stays optional — but the copy never claims one is saved.
 */
export function isConfigFieldRequired(field: SettlementConfigField, editing: boolean): boolean {
  if (!field.required) return false;
  return !(field.secret && editing);
}

export function configFieldPlaceholder(field: SettlementConfigField, editing: boolean): string {
  return field.secret && editing ? 'Leave blank to keep the current value, if one was saved' : `Enter ${field.label}`;
}

/**
 * Shown under a required secret on edit: if none was ever saved, leaving it
 * blank keeps nothing and payments through this account fail.
 */
export function secretEditWarning(field: SettlementConfigField, editing: boolean, noun: string): string {
  if (!editing || !field.secret || !field.required) return '';
  return `If a ${field.label} was never saved for this ${noun}, payments through it will fail — paste it here to be sure.`;
}

/**
 * Config to send: the filled-in form fields, plus any extra non-secret values
 * already stored that this form no longer shows (e.g. an old "environment"),
 * so editing never silently drops them. Blank secrets are left out — the
 * server keeps the saved ones.
 */
export function buildSettlementConfig(
  fields: SettlementConfigField[],
  values: Record<string, string>,
  existingConfig: unknown = null,
): Record<string, string> {
  const config: Record<string, string> = {};
  const formKeys = new Set(fields.map((field) => field.key));
  if (existingConfig && typeof existingConfig === 'object') {
    for (const [key, value] of Object.entries(existingConfig as Record<string, unknown>)) {
      if (!formKeys.has(key) && typeof value === 'string' && value !== '') config[key] = value;
    }
  }
  for (const field of fields) {
    const value = (values[field.key] ?? '').trim();
    if (value) config[field.key] = value;
  }
  return config;
}

/** Bank fields for the payload: blank means "leave out" on create but "clear it" (null) on update. */
export function bankFieldsPayload(
  form: { bank_name: string; account_number: string; account_name: string },
  editing: boolean,
): { bank_name?: string | null; account_number?: string | null; account_name?: string | null } {
  const value = (raw: string) => {
    const trimmed = raw.trim();
    if (trimmed) return trimmed;
    return editing ? null : undefined;
  };
  return {
    bank_name: value(form.bank_name),
    account_number: value(form.account_number.replace(/\s+/g, '')),
    account_name: value(form.account_name),
  };
}

/** "Merchant: MX123 · PAY Item: 101" for Interswitch; the gateway identifier otherwise. */
export function accountSummary(account: Pick<GatewaySettlementAccount, 'config' | 'gateway_identifier'>, providerName: string | null | undefined): string {
  const config = account.config as Record<string, string> | null;
  if (!config || normalizeCode(providerName) !== 'interswitch') return account.gateway_identifier;
  const parts: string[] = [];
  if (config.merchantCode) parts.push(`Merchant: ${config.merchantCode}`);
  if (config.payItemId) parts.push(`PAY Item: ${config.payItemId}`);
  return parts.join(' · ') || account.gateway_identifier;
}

const DUPLICATE_IDENTIFIER = /gateway identifier already exists/i;
const DEFAULT_NOT_DELETABLE = /cannot delete the default/i;
const INACTIVE_NOT_DEFAULT = /inactive settlement account as default/i;

/** True when the server refused because the identifier (e.g. PAY Item ID) is already used. */
export function isDuplicateIdentifierError(error: unknown): boolean {
  const message = (error as { message?: unknown } | null)?.message;
  return typeof message === 'string' && DUPLICATE_IDENTIFIER.test(message);
}

/** Toast detail for a failed save/remove, in plain words. */
export function settlementAccountErrorMessage(error: unknown, noun: string): string {
  const message = (error as { message?: unknown } | null)?.message;
  const text = typeof message === 'string' ? message : '';
  if (DUPLICATE_IDENTIFIER.test(text)) return `That ID is already used by another ${noun}. Use a different one, or edit the existing ${noun}.`;
  if (DEFAULT_NOT_DELETABLE.test(text)) return `This is the default ${noun}, so it can't be removed. Make another ${noun} the default first.`;
  if (INACTIVE_NOT_DEFAULT.test(text)) return `A switched-off ${noun} can't be the default.`;
  if (firstValidationMessage(error)) return 'Some details need fixing — see the highlighted fields.';
  return toUserMessage(error, 'Nothing was changed. Please try again.');
}

export function deleteAccountConfirmMessage(label: string, noun: string): string {
  return `Remove the ${noun} "${label}"? You won't be able to choose it for fees any more. Bills already set to pay into it are not changed. This can't be undone.`;
}

// ── "Pays into" dropdown (SettlementAccountSelect.vue) ──

// @allow-handwritten UI-only dropdown option; never crosses the wire
export interface SettlementAccountOption {
  id: string;
  label: string;
  providerLabel: string;
  is_default: boolean;
}

/**
 * Options for choosing which bank account a fee pays into. Switched-off
 * accounts are left out (a fee pointed at one would fail at checkout) unless
 * the caller lists history, e.g. a report filter.
 */
export function settlementAccountOptions(
  accounts: Array<Pick<GatewaySettlementAccount, 'id' | 'label' | 'is_default' | 'is_active'> & { paymentProvider?: { name?: string | null } | null }>,
  providerLabelFor: (name: string | null | undefined) => string,
  options: { includeInactive?: boolean } = {},
): SettlementAccountOption[] {
  return accounts
    .filter((account) => options.includeInactive || account.is_active)
    .map((account) => ({
      id: account.id,
      label: account.is_active ? account.label : `${account.label} (switched off)`,
      providerLabel: account.paymentProvider?.name ? providerLabelFor(account.paymentProvider.name) : '',
      is_default: account.is_default,
    }));
}

/** Text for the selected value — never a raw id. */
export function selectedSettlementLabel(id: string | null | undefined, options: SettlementAccountOption[], loading: boolean): string {
  if (!id) return '';
  const match = options.find((option) => option.id === id);
  if (match) return match.label;
  return loading ? 'Loading…' : 'Account no longer available — choose another';
}
